import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StanButton, StanInput, StanModal, StanPhoneInput } from '@/components';
import type { PhoneChange } from '@/components/core-components/StanPhoneInput/StanPhoneInput.types';
import { useAddContact } from '../queries';
import { type AddContactFormData, addContactSchema } from '../validations/addContact.validation';

interface AddContactModalProps {
    isOpen: boolean;
    onClose: () => void;
    onFileSelected: (file: File, count: number) => void;
}

export function AddContactModal({ isOpen, onClose, onFileSelected }: AddContactModalProps) {
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<AddContactFormData>({
        resolver: zodResolver(addContactSchema),
        defaultValues: {
            name: '',
            email: '',
            phoneNumber: '',
        },
    });

    const { mutate: addContact, isPending: isAdding } = useAddContact();

    const onSubmit = (data: AddContactFormData) => {
        addContact(
            {
                name: data.name,
                email: data.email,
                phoneNumber: data.phoneNumber,
            },
            {
                onSuccess: () => {
                    reset();
                    onClose();
                },
            }
        );
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Read CSV file to count contacts
            try {
                const text = await file.text();
                const lines = text.trim().split('\n');
                // Subtract 1 for header row
                const count = Math.max(0, lines.length - 1);

                // Close the main modal before showing confirmation
                onClose();
                // Notify parent to show confirmation modal
                onFileSelected(file, count);
            } catch (error) {
                console.error('Error reading CSV file:', error);
            }
        }
        // Reset the input value so the same file can be selected again
        event.target.value = '';
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <StanModal id='add-contact-modal' isOpen={isOpen} onClose={handleClose} title='Add a contact...' size='sm'>
            <div className='flex flex-col gap-6'>
                <p className='text-gray text-label-sm-regular -mt-2 text-center'>Manually enter customer details</p>

                {/* Manual Entry Form */}
                <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4'>
                    <Controller
                        name='name'
                        control={control}
                        render={({ field }) => (
                            <StanInput {...field} placeholder='Name' error={!!errors.name} disabled={isAdding} />
                        )}
                    />
                    {errors.name && (
                        <p className='text-negative-active text-label-xs-bold -mt-2'>{errors.name.message}</p>
                    )}

                    <Controller
                        name='email'
                        control={control}
                        render={({ field }) => (
                            <StanInput {...field} placeholder='Email' error={!!errors.email} disabled={isAdding} />
                        )}
                    />
                    {errors.email && (
                        <p className='text-negative-active text-label-xs-bold -mt-2'>{errors.email.message}</p>
                    )}

                    <Controller
                        name='phoneNumber'
                        control={control}
                        render={({ field }) => (
                            <StanPhoneInput
                                id='contact-phone'
                                name='phoneNumber'
                                value={field.value || ''}
                                onChange={(phoneChange: PhoneChange) => {
                                    // Extract the E.164 formatted phone number (or national if invalid)
                                    field.onChange(phoneChange.e164 || phoneChange.national || '');
                                }}
                                disabled={isAdding}
                                error={!!errors.phoneNumber}
                                placeholder='Phone Number'
                            />
                        )}
                    />
                    {errors.phoneNumber && (
                        <p className='text-negative-active text-label-xs-bold -mt-2'>{errors.phoneNumber.message}</p>
                    )}

                    <StanButton
                        name='add-contact-button'
                        type='submit'
                        disabled={isAdding}
                        loading={isAdding}
                        className='w-full'
                    >
                        Add Contact
                    </StanButton>
                </form>

                {/* CSV Import Section */}
                <div className='flex flex-col gap-4'>
                    <h3 className='text-dark text-label-md-bold text-center'>...or import a list</h3>

                    <p className='text-gray text-label-sm-regular text-center'>
                        Please upload a CSV file with the following columns: Email Address, First Name, Last Name, and
                        Phone Number.
                    </p>
                    <p className='text-gray text-label-xs-regular text-center'>
                        Maximum of 5,000 contacts per account.
                    </p>

                    <input
                        type='file'
                        accept='.csv'
                        onChange={handleFileChange}
                        className='hidden'
                        id='csv-upload'
                        disabled={isAdding}
                    />

                    <StanButton
                        name='upload-csv-button'
                        color='secondary'
                        disabled={isAdding}
                        onClick={() => document.getElementById('csv-upload')?.click()}
                        className='w-full'
                    >
                        Upload
                    </StanButton>
                </div>
            </div>
        </StanModal>
    );
}
